import type { StageStatus } from '@/types/campaign'
import {
  CheckCircle,
  Circle,
  Clock,
  FileText,
  HourglassMedium,
  Pause,
  Play,
  Prohibit,
  WarningCircle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import {
  badgeDotColors,
  badgeVariantBorderStyles,
  badgeVariantIconTint,
  badgeVariantStyles,
  getBadgeVariant,
  type BadgeVariant,
} from './utils'

/**
 * The campaign lifecycle state map (§60) — the ONE place a campaign state's
 * label, glyph and tone are decided.
 *
 * Before this module the same seven states carried five different vocabularies
 * (`lib/utils` badge map, the CampaignsPage row chip, the CampaignWorkflowFlow
 * stage pill, the CampaignDetailPage header and the LaunchCampaignModal row
 * pill). `completed` rendered muted, indigo AND emerald on three surfaces;
 * `stopped`/`failed` got an indigo "send" glyph two pixels from a red badge, so
 * a failed campaign looked like it was still sending.
 *
 * Colour is never declared here — it is read from `statusToBadge` in
 * `lib/utils`, which stays the single tone authority. This module adds only the
 * two things a tone cannot express: the human label and the glyph.
 *
 *   in flight    → `info`        indigo   running / stage Active
 *   finished ok  → `success`     emerald  completed / stage Done
 *   held, queued → `warning`     amber    scheduled, paused / stage Held
 *   terminal bad → `destructive` red      stopped, failed, rejected
 *   inert        → `default`     muted    draft / stage Not started
 *
 * `scheduled` and `paused` deliberately share amber — both mean "not sending
 * right now, by intent". They are told apart by glyph (Clock vs Pause) and
 * label, which is why every surface must render the icon this module supplies.
 */
export type CampaignStatusKey =
  | 'draft'
  | 'rejected'
  | 'pending_approval'
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'completed'
  | 'stopped'
  | 'failed'

/**
 * Label + glyph per state. `draft` is not in the `CampaignStatus` union the API
 * declares, but the backend's enum and `statusToBadge` both carry it, so it is
 * handled rather than falling through to the unknown branch.
 */
const CAMPAIGN_STATUS_META: Record<
  CampaignStatusKey,
  { label: string; Icon: PhosphorIcon; iconNudge?: string }
> = {
  draft: { label: 'Draft', Icon: FileText },
  // An Owner/Admin/Manager declined a Rep's launch request. Terminal-bad red,
  // same family as stopped/failed -- editable and re-requestable, but never
  // silently relabeled "Draft" (that read as nothing happened at all).
  rejected: { label: 'Rejected', Icon: Prohibit },
  // A Rep asked to launch this; an Owner/Admin/Manager must approve it in
  // Settings → Campaign Approvals. Amber like scheduled/paused ("not sending
  // right now, by intent"), told apart by the hourglass glyph and label.
  pending_approval: { label: 'Pending approval', Icon: HourglassMedium },
  scheduled: { label: 'Scheduled', Icon: Clock },
  // Phosphor's Play glyph is optically left-heavy inside a square chip.
  running: { label: 'Running', Icon: Play, iconNudge: 'translate-x-[1px]' },
  paused: { label: 'Paused', Icon: Pause },
  completed: { label: 'Completed', Icon: CheckCircle },
  stopped: { label: 'Stopped', Icon: Prohibit },
  failed: { label: 'Failed', Icon: WarningCircle },
}

export const CAMPAIGN_STATUS_KEYS = Object.keys(CAMPAIGN_STATUS_META) as CampaignStatusKey[]

export function isCampaignStatus(status: string): status is CampaignStatusKey {
  return status in CAMPAIGN_STATUS_META
}

/** "on_hold" / "PARTIALLY_SENT" → "On hold" / "Partially sent". */
function humanizeStatus(status: string): string {
  const spaced = status.replace(/[_-]+/g, ' ').trim().toLowerCase()
  if (!spaced) return 'Unknown'
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export interface CampaignStatusMeta {
  /** The matched state, or `'unknown'` for anything the API adds later. */
  key: CampaignStatusKey | 'unknown'
  label: string
  variant: BadgeVariant
  Icon: PhosphorIcon
  /** Optical alignment class for the glyph — `''` when none is needed. */
  iconNudge: string
  /** Soft chip classes (bg + text), no border. */
  chipClass: string
  /** Soft chip classes with a matching border, for pills on a card surface. */
  borderChipClass: string
  /** Status dot. */
  dotClass: string
  /** Icon-chip tint: `bg` for the chip, `fg` for the glyph. */
  iconTint: { bg: string; fg: string }
}

/**
 * Everything a surface needs to render one campaign state. An unrecognised or
 * missing status degrades to a muted chip labelled with the humanized raw value
 * rather than silently inheriting another state's colour.
 */
export function campaignStatusMeta(status?: string | null): CampaignStatusMeta {
  const raw = (status || '').toLowerCase()
  const known = isCampaignStatus(raw)
  const meta = known ? CAMPAIGN_STATUS_META[raw] : null
  // Unknown states get the muted tone, never a borrowed one.
  const variant: BadgeVariant = known ? getBadgeVariant(raw) : 'default'

  return {
    key: known ? raw : 'unknown',
    label: meta ? meta.label : humanizeStatus(raw),
    variant,
    Icon: meta ? meta.Icon : Circle,
    iconNudge: meta?.iconNudge ?? '',
    chipClass: badgeVariantStyles[variant],
    borderChipClass: badgeVariantBorderStyles[variant],
    dotClass: badgeDotColors[variant],
    iconTint: badgeVariantIconTint[variant],
  }
}

/**
 * Heading for the campaign-level failure/blocked banner (Campaign Detail
 * page) and the reason tooltip (Campaigns list chip). Only the four states
 * that can carry a `last_error_reason` (backend: `get_campaign_blocking_reason`
 * in campaign_error_reason.py) get one — everything else, including a
 * healthy `scheduled` campaign with no reason, returns null.
 *
 * `scheduled` gets its own copy rather than reusing the "Scheduled" label:
 * a plan/credit gate leaves the campaign silently retrying in `scheduled`
 * forever (see campaign_launcher.py), so the banner must not read as the
 * healthy queued state.
 */
const BLOCKING_BANNER_HEADING: Partial<Record<CampaignStatusKey, string>> = {
  failed: 'Campaign failed',
  stopped: 'Campaign stopped',
  paused: 'Campaign paused',
  scheduled: 'Campaign blocked',
}

export function campaignBlockingBannerHeading(key: CampaignStatusKey | 'unknown'): string | null {
  if (key === 'unknown') return null
  return BLOCKING_BANNER_HEADING[key] ?? null
}

// -----------------------------------------------------------------------------
// Stage level — the same five tones, one level down.

/**
 * The four visual states one workflow stage can be in, derived from its live
 * recipient counts. Named for what they mean rather than for the backend enum:
 * the API's `waiting` execution state means "actively held on a delay", the
 * exact opposite of "nobody has reached this stage yet", so that word is not
 * used here or in the UI (§60).
 */
export type CampaignStageVisual = 'active' | 'held' | 'done' | 'failed' | 'not_started'

/**
 * Stage tone ← campaign tone. `active` matches `running` and `done` matches
 * `completed` **by construction**, which is what stops the two levels from
 * swapping meanings the way they used to (stage Active was indigo while
 * campaign running was emerald, and stage Done was emerald while campaign
 * completed was indigo — so a reader who learned one level misread the other).
 */
export const STAGE_VISUAL_VARIANT: Record<CampaignStageVisual, BadgeVariant> = {
  active: getBadgeVariant('running'),
  held: getBadgeVariant('paused'),
  done: getBadgeVariant('completed'),
  failed: getBadgeVariant('failed'),
  not_started: 'default',
}

export const STAGE_VISUAL_LABEL: Record<CampaignStageVisual, string> = {
  active: 'Active',
  held: 'Held',
  done: 'Done',
  failed: 'Failed',
  not_started: 'Not started',
}

/** Legend hint per stage state — spells out what the tone means on the canvas. */
export const STAGE_VISUAL_HINT: Record<CampaignStageVisual, string> = {
  active: 'Active now',
  held: 'Held for recipient timezone',
  done: 'Completed',
  failed: 'Failed for every recipient',
  not_started: 'Not reached yet',
}

/**
 * Which visual a live stage gets. Held wins over active (a held row is a
 * subset of the active ones); a stage the backend marked 'failed' (nobody got
 * through it) is red, not Done.
 */
export function stageVisual(stage?: {
  status: StageStatus
  held: number
  in_progress: number
} | null): CampaignStageVisual {
  if (!stage) return 'not_started'
  if (stage.held > 0) return 'held'
  if (stage.status === 'in_progress' && stage.in_progress > 0) return 'active'
  if (stage.status === 'failed') return 'failed'
  if (stage.status === 'completed') return 'done'
  return 'not_started'
}

/** Soft chip classes for a stage state, from the shared tone table. */
export function stageVisualChipClass(visual: CampaignStageVisual): string {
  return badgeVariantStyles[STAGE_VISUAL_VARIANT[visual]]
}

/** Legend / breakdown dot for a stage state. */
export function stageVisualDotClass(visual: CampaignStageVisual): string {
  return badgeDotColors[STAGE_VISUAL_VARIANT[visual]]
}
